import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import {
  cancelSalesOrder,
  createSalesOrder,
  getSalesOrder,
  patchSalesOrderDraft,
  postCustomerPayment,
  postSalesDelivery,
  submitSalesOrder,
  voidSalesOrder,
} from "@/shared/api/sales";
import { listReturns } from "@/shared/api/returns";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { PayMethodSelect } from "@/modules/core/components/PayMethodSelect";
import { WarehouseSelect } from "@/modules/inventory/components/WarehouseSelect";
import { DeskDocTabs } from "@/modules/shell/DeskDocTabs";
import { DeskDocActionRail } from "@/modules/shell/DeskDocActionRail";
import { DocActivity } from "@/modules/shell/DocActivity";
import { EmptyState } from "@/modules/shell/EmptyState";
import { DataTable } from "@/modules/shell/data-table";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import {
  asCalendarYmd,
  formatDocDateLabel,
} from "@/modules/shell/relative-time";
import { useDeskDocTab } from "@/modules/shell/use-desk-doc-tab";
import { useRecordRecentDoc } from "@/modules/shell/use-record-recent-doc";
import { DocSlip, printDocSlip } from "@/modules/docs/print/DocSlip";
import { buildSoPrint } from "@/modules/docs/print/doc-print-math";
import { apiErrorCode, isApiError } from "@/shared/api/api-error";
import { newClientIdempotencyKey } from "@/shared/client-idempotency";
import { notify } from "@/shared/feedback/notify";
import { queryKeys } from "@/shared/query/keys";
import { moneyDisplay, primaryDueBadge } from "@icraftx/shared/v1";
import { AxisProgress } from "@/modules/core/components/AxisProgress";
import { LifecycleStatus } from "@/modules/core/components/LifecycleStatus";
import {
  soDeliveryColumns,
  soLineColumns,
  soPaymentColumns,
  soReturnColumns,
} from "./so-detail-columns";

const SO_DOC_TABS = ["order", "deliveries", "payments", "returns"] as const;

export function SalesOrderDetailPage() {
  const { companySlug, orderId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [data, setData] = useState<Awaited<
    ReturnType<typeof getSalesOrder>
  > | null>(null);
  const [delivQty, setDelivQty] = useState<Record<string, string>>({});
  const [delivLot, setDelivLot] = useState<Record<string, string>>({});
  const [delivSerial, setDelivSerial] = useState<Record<string, string>>({});
  const [payAmount, setPayAmount] = useState("");
  const [payMethodId, setPayMethodId] = useState<string | null>(null);
  const [bankRef, setBankRef] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draftRef, setDraftRef] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [draftPayDue, setDraftPayDue] = useState("");
  const [draftDeliverBy, setDraftDeliverBy] = useState("");
  const [draftDocDate, setDraftDocDate] = useState("");

  async function reload() {
    if (!companySlug || !orderId) return;
    const d = await getSalesOrder(companySlug, orderId);
    setData(d);
    const init: Record<string, string> = {};
    for (const line of d.lines) {
      init[line.id] = line.qtyOpen;
    }
    setDelivQty(init);
    const outstanding = (
      Number(d.order.total) - Number(d.order.amountPaid)
    ).toFixed(2);
    setPayAmount(outstanding);
    setDraftRef(d.order.customerRef ?? "");
    setDraftNotes(d.order.notes ?? "");
    setDraftPayDue(d.order.paymentDueOn ?? "");
    setDraftDeliverBy(d.order.deliverBy ?? "");
    setDraftDocDate(asCalendarYmd(d.order.docDate));
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : "Failed";
      if (msg === "Not signed in") navigate("/login", { replace: true });
      else setError(msg);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, orderId, navigate]);

  const [docTab, setDocTab] = useDeskDocTab("order", SO_DOC_TABS);
  useRecordRecentDoc({
    companySlug,
    kind: "salesOrder",
    id: data?.order.id,
    label: data?.order.number,
    path:
      companySlug && data?.order.id
        ? `/c/${companySlug}/sales/orders/${data.order.id}`
        : undefined,
    hint: data?.order.customerName,
  });
  const relatedReturnsQuery = useQuery({
    queryKey: queryKeys.returns.list(companySlug ?? "", {
      sourceType: "sales_order",
      sourceId: orderId,
      limit: 100,
    }),
    enabled: Boolean(companySlug && orderId),
    queryFn: () =>
      listReturns(companySlug!, {
        sourceType: "sales_order",
        sourceId: orderId!,
        limit: 100,
      }),
  });

  if (!companySlug || !orderId) return null;
  if (!data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading sales order…</Alert>
        )}
      </PageFrame>
    );
  }

  const { order, lines, deliveries, payments } = data;
  const relatedReturns = relatedReturnsQuery.data?.returns ?? [];
  const printData = buildSoPrint({
    number: order.number,
    customerName: order.customerName,
    lifecycle: order.lifecycle,
    pay: order.pay,
    deliver: order.deliver,
    discountPct: order.discountPct,
    netTotal: order.netTotal,
    taxTotal: order.taxTotal,
    grossTotal: order.grossTotal ?? order.total,
    amountPaid: order.amountPaid,
    customerRef: order.customerRef,
    lines: lines.map((l) => ({
      productName: l.productName,
      qtyOrdered: l.qtyOrdered,
      unitPrice: l.unitPrice,
      lineGross: l.lineGross,
    })),
  });

  async function onDeliver() {
    setBusy(true);
    setError(null);
    try {
      const payload = lines
        .map((l) => ({
          salesOrderLineId: l.id,
          qty: delivQty[l.id] ?? "0",
          ...(l.trackLot && delivLot[l.id]?.trim()
            ? { lotCode: delivLot[l.id]!.trim() }
            : {}),
          ...(l.trackSerial && delivSerial[l.id]?.trim()
            ? { serialCode: delivSerial[l.id]!.trim() }
            : {}),
        }))
        .filter((l) => Number(l.qty) > 0);
      if (payload.length === 0) throw new Error("Enter qty to deliver");

      type DeliverOpts = {
        lines: typeof payload;
        warehouseId?: string;
        confirmOverDeliver?: boolean;
        idempotencyKey: string;
      };

      /** One key for soft-confirm retries so a flaky retry cannot double-ship — plan/24 */
      const idempotencyKey = newClientIdempotencyKey("so-deliv");

      const attempt = async (opts: DeliverOpts) => {
        try {
          return await postSalesDelivery(companySlug!, orderId!, opts);
        } catch (err: unknown) {
          const code = apiErrorCode(err);
          if (
            code === "over_deliver" &&
            isApiError(err) &&
            !opts.confirmOverDeliver
          ) {
            const ok = await confirmUi({
              title: "Over-deliver?",
              description: `${err.message}\n\nDeliver anyway? Extra qty is beyond the order line.`,
              confirmLabel: "Deliver anyway",
              destructive: true,
            });
            if (!ok) {
              throw new Error("Deliver cancelled");
            }
            return attempt({ ...opts, confirmOverDeliver: true });
          }
          throw err;
        }
      };

      const result = await attempt({
        lines: payload,
        idempotencyKey,
        ...(warehouseId ? { warehouseId } : {}),
      });
      if (result.oversell) {
        notify.info("Delivered — below recorded on-hand (audited)");
      } else {
        notify.success("Delivery posted");
      }
      navigate(`/c/${companySlug}/sales/deliveries/${result.delivery.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Deliver failed");
    } finally {
      setBusy(false);
    }
  }

  async function onPay(direction: "from_customer" | "to_customer") {
    setBusy(true);
    setError(null);
    try {
      if (direction === "to_customer") {
        const paid = Number(order.amountPaid);
        const amt = Number(payAmount);
        if (!(paid > 0)) {
          setError("Nothing collected yet to refund or credit against");
          return;
        }
        if (amt > paid) {
          const ok = await confirmUi({
            title: "Refund exceeds collected?",
            description: `Refund/credit ${payAmount} exceeds collected ${order.amountPaid}. Continue?`,
            confirmLabel: "Continue",
            destructive: true,
          });
          if (!ok) {
            setError("Refund cancelled");
            return;
          }
        }
      }
      const idempotencyKey = newClientIdempotencyKey("so-pay");
      const result = await postCustomerPayment(companySlug!, {
        customerId: order.customerId,
        salesOrderId: order.id,
        amount: payAmount,
        direction,
        payMethodId: payMethodId ?? undefined,
        ...(bankRef.trim() ? { bankRef: bankRef.trim() } : {}),
        idempotencyKey,
      });
      if (result.overpay) {
        notify.info(
          "Collected over balance — extra kept as unapplied customer credit",
        );
      } else {
        notify.success("Payment recorded");
      }
      navigate(`/c/${companySlug}/sales/payments/${result.payment.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Payment failed");
    } finally {
      setBusy(false);
    }
  }

  async function onLifecycle(
    action: "submit" | "cancel" | "void",
  ) {
    const number = data?.order.number;
    if (action === "cancel") {
      const ok = await confirmUi({
        title: `Cancel ${number ?? "draft"}?`,
        description: "The draft will be voided. This cannot be undone.",
        confirmLabel: "Cancel draft",
        destructive: true,
      });
      if (!ok) return;
    }
    if (action === "void") {
      const ok = await confirmUi({
        title: `Void ${number ?? "sales order"}?`,
        description:
          "Voiding is permanent — you cannot reopen this order. Deliveries and payments must already be clear.",
        confirmLabel: "Void order",
        destructive: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    setError(null);
    try {
      if (action === "submit") await submitSalesOrder(companySlug!, orderId!);
      else if (action === "cancel")
        await cancelSalesOrder(companySlug!, orderId!);
      else await voidSalesOrder(companySlug!, orderId!);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Lifecycle failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDuplicate() {
    setBusy(true);
    setError(null);
    try {
      const taxMode =
        order.taxPriceMode === "inclusive" || order.taxPriceMode === "exclusive"
          ? order.taxPriceMode
          : undefined;
      const { order: draft } = await createSalesOrder(companySlug!, {
        customerId: order.customerId,
        lines: lines.map((l) => ({
          productId: l.productId,
          qty: l.qtyOrdered,
          ...(l.unitPrice.trim() ? { unitPrice: l.unitPrice } : {}),
          ...(l.discountPct.trim() ? { discountPct: l.discountPct } : {}),
        })),
        submit: false,
        ...(order.discountPct.trim() ? { discountPct: order.discountPct } : {}),
        ...(order.taxRatePct.trim() ? { taxRatePct: order.taxRatePct } : {}),
        ...(taxMode ? { taxPriceMode: taxMode } : {}),
        ...(order.customerRef?.trim()
          ? { customerRef: order.customerRef.trim() }
          : {}),
        ...(order.paymentDueOn ? { paymentDueOn: order.paymentDueOn } : {}),
        ...(order.deliverBy ? { deliverBy: order.deliverBy } : {}),
      });
      notify.success(`Draft ${draft.number} created`);
      navigate(`/c/${companySlug}/sales/orders/${draft.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Duplicate failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageFrame width="narrow">
      <div className="pos-receipt-print" aria-hidden>
        <DocSlip data={printData} />
      </div>
      <div className="no-print">
        <div className="mb-3">
          <DeskDocBackLink
            listBasePath={`/c/${companySlug}/sales/orders`}
            listLabel="Sales orders"
          />
        </div>
        <PageHeader
          eyebrow="Sales order"
          title={order.number}
          description={[
            order.customerName,
            order.customerRef ? `ref ${order.customerRef}` : null,
            order.docDate
              ? `Date ${formatDocDateLabel(order.docDate)}`
              : null,
            (() => {
              const due = primaryDueBadge({
                paymentDueOn: order.paymentDueOn,
                deliverBy: order.deliverBy,
              });
              return due.label || null;
            })(),
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => void onDuplicate()}
              >
                Duplicate
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => printDocSlip()}
              >
                Print
              </Button>
            </div>
          }
        />
        <div className="mt-2">
          <LifecycleStatus value={order.lifecycle} showLabel />
        </div>
        <div className="mt-3 flex flex-wrap gap-6">
          <AxisProgress
            className="min-w-[10rem] max-w-xs flex-1"
            label="Paid"
            axis="Paid"
            format="money"
            current={order.amountPaid}
            max={order.total || order.grossTotal}
          />
          <AxisProgress
            className="min-w-[10rem] max-w-xs flex-1"
            label="Delivered"
            axis="Delivered"
            format="qty"
            current={order.qtyDelivered}
            max={order.qtyOrdered}
          />
        </div>
        <TagsAssigneesPanel
          companySlug={companySlug}
          docType="sales_order"
          docId={orderId}
          linkPath={`/c/${companySlug}/sales/orders/${orderId}`}
        />
        {order.lifecycle === "open" ? (
          <DeskDocActionRail className="mt-4">
            <Button
              type="button"
              size="sm"
              disabled={busy}
              onClick={() => void onDeliver()}
            >
              Post delivery
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !payAmount.trim()}
              onClick={() => void onPay("from_customer")}
            >
              Collect
            </Button>
          </DeskDocActionRail>
        ) : null}
        <DeskDocTabs
          aria-label="Sales order views"
          value={docTab}
          onChange={setDocTab}
          items={[
            { id: "order", label: "Order" },
            {
              id: "deliveries",
              label: "Deliveries",
              count: deliveries.length,
            },
            {
              id: "payments",
              label: "Payments",
              count: payments.length,
            },
            {
              id: "returns",
              label: "Returns",
              count: relatedReturns.length,
            },
          ]}
        />
      </div>

      {docTab === "order" &&
        (order.lifecycle === "draft" || order.lifecycle === "open") && (
        <form
          className="no-print mt-4 space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/60 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void (async () => {
              setBusy(true);
              setError(null);
              setStatus(null);
              try {
                const save = async (confirmOverBackdate?: boolean) => {
                  try {
                    if (order.lifecycle === "draft") {
                      await patchSalesOrderDraft(companySlug, orderId, {
                        customerRef: draftRef.trim() || null,
                        notes: draftNotes.trim() || null,
                        paymentDueOn: draftPayDue.trim() || null,
                        deliverBy: draftDeliverBy.trim() || null,
                        ...(draftDocDate.trim()
                          ? { docDate: draftDocDate.trim() }
                          : {}),
                        ...(confirmOverBackdate
                          ? { confirmOverBackdate: true }
                          : {}),
                      });
                    } else {
                      await patchSalesOrderDraft(companySlug, orderId, {
                        docDate: draftDocDate.trim(),
                        ...(confirmOverBackdate
                          ? { confirmOverBackdate: true }
                          : {}),
                      });
                    }
                  } catch (err: unknown) {
                    if (
                      apiErrorCode(err) === "doc_date_backdate" &&
                      isApiError(err) &&
                      !confirmOverBackdate
                    ) {
                      const ok = await confirmUi({
                        title: "Backdated document date?",
                        description: err.message,
                        confirmLabel: "Save anyway",
                      });
                      if (!ok) throw new Error("Save cancelled");
                      return save(true);
                    }
                    throw err;
                  }
                };
                await save();
                setStatus(
                  order.lifecycle === "draft"
                    ? "Updated draft"
                    : "Updated date",
                );
                notify.updated(order.number);
                await reload();
              } catch (err: unknown) {
                const msg =
                  err instanceof Error ? err.message : "Save failed";
                setError(msg);
                notify.error(msg);
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          <h2 className="m-0 text-sm font-semibold">
            {order.lifecycle === "draft" ? "Edit draft" : "Edit date"}
          </h2>
          {order.lifecycle === "draft" ? (
            <>
              <label className="block text-xs text-[var(--color-muted)]">
                Customer ref
                <Input
                  className="mt-1"
                  value={draftRef}
                  onChange={(e) => setDraftRef(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="block text-xs text-[var(--color-muted)]">
                Notes
                <Input
                  className="mt-1"
                  value={draftNotes}
                  onChange={(e) => setDraftNotes(e.target.value)}
                  disabled={busy}
                />
              </label>
            </>
          ) : null}
          <div className="flex flex-wrap gap-3">
            <label className="block text-xs text-[var(--color-muted)]">
              Date
              <Input
                type="date"
                className="mt-1"
                value={draftDocDate}
                onChange={(e) => setDraftDocDate(e.target.value)}
                disabled={busy}
                required={order.lifecycle === "open"}
              />
            </label>
            {order.lifecycle === "draft" ? (
              <>
                <label className="block text-xs text-[var(--color-muted)]">
                  Payment due
                  <Input
                    type="date"
                    className="mt-1"
                    value={draftPayDue}
                    onChange={(e) => setDraftPayDue(e.target.value)}
                    disabled={busy}
                  />
                </label>
                <label className="block text-xs text-[var(--color-muted)]">
                  Deliver by
                  <Input
                    type="date"
                    className="mt-1"
                    value={draftDeliverBy}
                    onChange={(e) => setDraftDeliverBy(e.target.value)}
                    disabled={busy}
                  />
                </label>
              </>
            ) : null}
          </div>
          <Button type="submit" size="sm" pending={busy} pendingLabel="Saving…">
            {order.lifecycle === "draft" ? "Save draft" : "Save date"}
          </Button>
        </form>
      )}

      {docTab === "order" && (
        <>
          {status && (
            <Alert variant="muted" className="no-print mt-3">
              {status}
            </Alert>
          )}

          {(order.lifecycle === "draft" || order.lifecycle === "open") && (
            <div className="no-print mt-4 flex flex-wrap gap-2">
              {order.lifecycle === "draft" && (
                <>
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() => void onLifecycle("submit")}
                  >
                    Submit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void onLifecycle("cancel")}
                  >
                    Cancel draft
                  </Button>
                </>
              )}
              {order.lifecycle === "open" &&
                order.deliver === "undelivered" &&
                order.pay === "unpaid" && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => void onLifecycle("void")}
                  >
                    Void order
                  </Button>
                )}
              {order.lifecycle === "open" && (
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link
                    to={`/c/${companySlug}/returns/new?sourceType=sales_order&sourceId=${orderId}`}
                    className="no-underline"
                  >
                    Return
                  </Link>
                </Button>
              )}
            </div>
          )}
          <p className="mt-2 font-mono text-sm">
            Total {order.grossTotal ?? order.total} · Paid {order.amountPaid}
            {order.taxRatePct != null && Number(order.taxRatePct) > 0
              ? ` · tax ${order.taxRatePct}% ${order.taxPriceMode ?? ""}`
              : null}
          </p>

          <DataTable
            listId="so-detail-lines"
            companySlug={companySlug}
            className="mt-8"
            columns={soLineColumns({
              canDeliver: order.lifecycle === "open",
              delivQty,
              setDelivQty,
              delivLot,
              setDelivLot,
              delivSerial,
              setDelivSerial,
            })}
            data={lines}
            getRowId={(r) => r.id}
            manualSorting={false}
            empty={
              <EmptyState
                title="No lines"
                description="This order has no product lines."
              />
            }
          />

          {order.lifecycle === "open" && (
            <div className="mt-6 flex flex-col gap-3">
              <WarehouseSelect
                companySlug={companySlug}
                value={warehouseId}
                onChange={setWarehouseId}
                disabled={busy}
                label="Deliver from"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={busy}
                  onClick={() => void onDeliver()}
                >
                  Post delivery
                </Button>
              </div>
            </div>
          )}

          {order.lifecycle === "open" && (
            <div className="mt-8 border-t border-[var(--color-border)] pt-6">
              <h2 className="m-0 text-sm font-semibold">Customer payment</h2>
              <label className="mt-3 block text-xs text-[var(--color-muted)]">
                Method
                <div className="mt-1">
                  <PayMethodSelect
                    companySlug={companySlug}
                    value={payMethodId}
                    onChange={setPayMethodId}
                    disabled={busy}
                  />
                </div>
              </label>
              <label className="mt-3 block text-xs text-[var(--color-muted)]">
                Bank / ref (optional)
                <Input
                  className="mt-1"
                  value={bankRef}
                  onChange={(e) => setBankRef(e.target.value)}
                  maxLength={120}
                />
              </label>
              {(() => {
                const outstanding =
                  Number(order.total) - Number(order.amountPaid);
                const amt = Number(payAmount);
                const willOverpay =
                  Number.isFinite(amt) &&
                  amt > 0 &&
                  Number.isFinite(outstanding) &&
                  amt > outstanding + 1e-9;
                if (!willOverpay) return null;
                return (
                  <Alert variant="destructive" className="mt-3 py-2 text-xs">
                    Amount exceeds outstanding{" "}
                    {moneyDisplay(String(outstanding))} — extra becomes
                    unapplied credit. Collect still goes through.
                  </Alert>
                );
              })()}
              <div className="mt-3 flex flex-wrap gap-2">
                <Input
                  className="min-w-[8rem] flex-1 font-mono text-right tabular-nums"
                  inputMode="decimal"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
                <Button
                  type="button"
                  disabled={busy || !payAmount.trim()}
                  onClick={() => void onPay("from_customer")}
                >
                  Collect
                </Button>
                {Number(order.amountPaid) > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy || !payAmount.trim()}
                    onClick={() => void onPay("to_customer")}
                  >
                    Record refund / credit
                  </Button>
                )}
              </div>
              {Number(order.amountPaid) > 0 && (
                <p className="mt-2 m-0 text-xs text-[var(--color-muted)]">
                  Refund / credit uses the same amount and method — plan/09 AR
                  symmetry with supplier from_supplier.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {docTab === "deliveries" && (
        <DataTable
          listId="so-detail-deliveries"
          companySlug={companySlug}
          columns={soDeliveryColumns(companySlug)}
          data={deliveries}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No deliveries yet"
              description="Post a delivery from the Order tab when ready."
            />
          }
        />
      )}

      {docTab === "payments" && (
        <DataTable
          listId="so-detail-payments"
          companySlug={companySlug}
          columns={soPaymentColumns(companySlug)}
          data={payments}
          getRowId={(r) => r.id}
          manualSorting={false}
          empty={
            <EmptyState
              title="No payments yet"
              description="Collect from the Order tab when the customer pays."
            />
          }
        />
      )}

      {docTab === "returns" && (
        <div className="mt-2">
          {relatedReturnsQuery.isPending ? (
            <Alert variant="muted">Loading returns…</Alert>
          ) : (
            <DataTable
              listId="so-detail-returns"
              companySlug={companySlug}
              columns={soReturnColumns(companySlug)}
              data={relatedReturns}
              getRowId={(r) => r.id}
              manualSorting={false}
              empty={
                <EmptyState
                  title="No returns yet"
                  description="Start a return from the Order tab if goods come back."
                  action={
                    order.lifecycle === "open" ? (
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to={`/c/${companySlug}/returns/new?sourceType=sales_order&sourceId=${orderId}`}
                          className="no-underline"
                        >
                          New return
                        </Link>
                      </Button>
                    ) : null
                  }
                />
              }
            />
          )}
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="sales_order"
        docId={orderId}
        linkPath={`/c/${companySlug}/sales/orders/${orderId}`}
      />
    </PageFrame>
  );
}
